//This file is AppBar.jsx

import React from 'react';
import { styled } from '@mui/material/styles';
import { alpha } from '@mui/material/styles';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import { Link, useNavigate } from 'react-router-dom';
import AccountCircle from '@mui/icons-material/AccountCircle';
import Avatar from '@mui/material/Avatar';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { ThemeContext, UserContext } from 'src/store/ContextStore';
import EventIcon from '@mui/icons-material/Event';
import MenuIcon from '@mui/icons-material/Menu';

import {
  APICONSTANTS,
  CONSTANTS,
  NOTE_TYPE_COLOURS
} from 'src/components/Constants';
import axios from 'src/axios';
import logo from 'src/assets/images/trakgeneLogo.png';

import AssistantIcon from '@mui/icons-material/Assistant';
import { useCopilot } from 'src/components/VirtualAssistant/copilot/CopilotContext';

import TranslationMenu from 'src/components/settings/TranslationMenu';
import UserGroupChipCluster from './UserGroupChipCluster';
import { useAutoTranslation } from 'src/hooks/useTranslate';
import AnnouncementsBanner from 'src/components/UI/announcements/AnnouncementsBanner';

import NotificationsIcon from '@mui/icons-material/Notifications';
import Badge from '@mui/material/Badge';
import Chip from '@mui/material/Chip';
import CloseIcon from '@mui/icons-material/Close';

import { Modal, Switch, Divider, Box } from '@mui/material';
import CustomDialog from 'src/components/UI/dialog/CustomDialog';
import ModalHeader from 'src/components/UI/appbar/appointment-modal/ModalHeader';

import AppointmentForm2 from 'src/components/UI/appbar/appointment-modal/AppointmentForm_Notification';
import QuickActionsMenu from 'src/components/UI/appbar/QuickActionsMenu';
import WhatsNewDialog from 'src/components/release-notes/WhatsNewDialog';
import useReleaseNotes from 'src/components/release-notes/useReleaseNotes';
import { clearWorkspace } from 'src/store/workspace/workspacePersistence';

const drawerWidth = 200;

const AppBarShift = styled(AppBar)(({ theme }) => ({
  marginLeft: drawerWidth,
  width: `calc(100% - ${drawerWidth}px)`,
  transition: `${theme.transitions.create(['width', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen
  })}, background-color 1s`
}));

const Offset = styled('div', {
  shouldForwardProp: (prop) => prop !== 'offset'
})(({ offset }) => ({
  minHeight: offset
}));

const Root = styled('div')({
  flexGrow: 1
});

const Logo = styled('img')(({ theme }) => ({
  height: 40,
  [theme.breakpoints.down('md')]: {
    display: 'none'
  }
}));

const SystemMessage = styled('div')({
  // Position the wrapper itself (not each inner Alert) so the stack of
  // announcements flows naturally and clicks on the close button reach
  // their handler instead of the sibling <Toolbar> that renders after
  // this element. zIndex puts the alert above the toolbar's flex area.
  position: 'absolute',
  top: '9px',
  left: '25%',
  right: '25%',
  margin: 'auto',
  zIndex: 1
});

const PEP = styled(Typography)(({ theme }) => ({
  [theme.breakpoints.down('md')]: {
    position: 'absolute',
    left: 56
  }
}));

const SmallLogo = styled('img')(({ theme }) => ({
  display: 'none',
  [theme.breakpoints.down('md')]: {
    display: 'block',
    height: 20
  }
}));

const StyledAppBar = styled(AppBar, {
  shouldForwardProp: (prop) =>
    !['navbar', 'navbarText', 'appearance', 'offset'].includes(prop)
})(({ theme, navbar, appearance, navbarText, offset }) => ({
  zIndex: theme.zIndex.drawer + 1,
  backgroundColor: `${navbar ?? appearance.color}!important`,
  transition: 'background-color 1s',
  color: navbarText ?? appearance.text,
  borderBottom: '1px solid #c1c1c170',
  boxShadow: 'none',
  height: offset
}));

const StyledLink = styled(Link, {
  shouldForwardProp: (prop) => !['navbarText', 'appearance'].includes(prop)
})(({ theme, navbarText, appearance }) => ({
  textDecoration: 'none !important',
  fontWeight: 'bolder',
  color: navbarText ?? appearance.text,
  [theme.breakpoints.down('md')]: {
    fontSize: 16
  }
}));

const StyledMenu = styled(Menu)(({ appearance }) => ({
  '& .MuiPaper-root': {
    backgroundColor: appearance.color,
    color: appearance.text,
    boxShadow: `3px 3px 15px ${appearance.shade}`
  }
}));

export default React.memo(function CustomAppBar(props) {
  let [isModalOpen, setOpenModal] = React.useState(false);
  const handleClose = () => {
    setOpenModal(false);
  };

  let [currentModel, setCurrentModel] = React.useState({});

  const {
    theme: {
      appearance,
      navbar,
      navbarText,
      logoURL,
      logoName,
      faviconURL,
      appbarHeight,
      accent
    }
  } = React.useContext(ThemeContext);

  const offset = appbarHeight ? appbarHeight : 65;

  const { user, setUser } = React.useContext(UserContext);

  const t = useAutoTranslation();

  const navigate = useNavigate();

  const isMinimal = Boolean(props.minimal);

  const fmtDate = (d) => {
    if (!d) return '';
    return new Date(d).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getNotificationDescription = (n) => {
    switch (n.type) {
      case 'Appointment':
        return [
          n.inviteeName ? `${n.inviteeName}` : '',
          n.appointmentDate
            ? new Date(n.appointmentDate).toLocaleString(undefined, {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                hour12: true
              })
            : '',
          n.clinicName || ''
        ]
          .filter(Boolean)
          .join(' · ');
      case 'Test':
        return [
          `Test: ${n.testName ?? ''}`,
          n.dateResultDue ? `Due ${fmtDate(n.dateResultDue)}` : '',
          n.lab || ''
        ]
          .filter(Boolean)
          .join(' · ');
      case 'Referral':
        return `Referral to ${n.referralTo ?? ''}`;
      case 'Contact':
        return [
          n.contactType ?? 'Contact',
          n.contactDate ? fmtDate(n.contactDate) : ''
        ]
          .filter(Boolean)
          .join(' · ');
      case 'Clinical Note':
        return [
          'Clinical note',
          n.reminderDate
            ? fmtDate(n.reminderDate)
            : n.clinicalNoteDate
            ? fmtDate(n.clinicalNoteDate)
            : ''
        ]
          .filter(Boolean)
          .join(' · ');
      case 'Sample':
        return [
          `Sample ${n.sampleType ?? ''}`,
          n.reminderDate || n.dateTaken
            ? fmtDate(n.reminderDate || n.dateTaken)
            : '',
          n.sampleHeldBy ? `held by ${n.sampleHeldBy}` : ''
        ]
          .filter(Boolean)
          .join(' · ');
      case 'Consent':
        return [
          `Consent ${n.consentType ?? ''}`,
          n.reminderDate || n.consentDate
            ? fmtDate(n.reminderDate || n.consentDate)
            : '',
          n.response ? `response: ${n.response}` : ''
        ]
          .filter(Boolean)
          .join(' · ');
      default:
        return n.type;
    }
  };

  const copilot = useCopilot();

  const [notifications, setNotifications] = React.useState([]);
  const [anchorElNotifications, setAnchorElNotifications] =
    React.useState(null);

  const isNotificationsMenuOpen = Boolean(anchorElNotifications);

  const [showAllNotifications, setShowAllNotifications] = React.useState(true);

  const myId = React.useMemo(
    () => String(user?.id || user?._id || ''),
    [user?.id, user?._id]
  );

  const displayedNotifications = React.useMemo(() => {
    if (showAllNotifications) return notifications;
    if (!myId) return notifications;
    return notifications.filter(
      (n) => !n.createdBy || String(n.createdBy) === myId
    );
  }, [notifications, showAllNotifications, myId]);

  const handleNotificationsOpen = (event) => {
    setAnchorElNotifications(event.currentTarget);
  };

  const handleNotificationsClose = () => {
    setAnchorElNotifications(null);
  };

  const handleOpenRecord = async (notification) => {
    const { familyId, familyMemberId, id, type: notifType } = notification;
    if (!familyId || !familyMemberId || !id) return;

    let param = '';
    switch (notifType) {
      case 'Appointment':
        param = `appointmentId=${id}`;
        break;
      case 'Test':
        param = `testId=${id}`;
        break;
      case 'Referral':
        param = `referralId=${id}`;
        break;
      case 'Contact':
        param = `contactId=${id}`;
        break;
      case 'Clinical Note':
        param = `clinicalNoteId=${id}`;
        break;
      case 'Sample':
        param = `sampleId=${id}`;
        break;
      case 'Consent':
        param = `consentId=${id}`;
        break;
      default:
        break;
    }

    const url =
      CONSTANTS.pedigreeChartPath.replace(':_id', familyId) +
      `?familyMemberId=${familyMemberId}&${param}`;

    window.location.href = url;
    setAnchorElNotifications(null);
  };

  const handleDismissNotification = (notificationId) => {
    setNotifications((prev) => prev.filter((n) => n.id !== notificationId));
  };

  // 2. Fetch notifications from the backend
  React.useEffect(() => {
    const fetchNotifications = async () => {
      const normalize = (items) =>
        (items || []).map((n) => ({
          ...n,
          createdBy: String(
            (n.createdBy && (n.createdBy._id || n.createdBy)) ||
              n.creatorId ||
              n.creator ||
              n.ownerId ||
              ''
          )
        }));

      const endpoints = [
        [
          '/clinical-notes/appointments/notifications?onlyFuture=true',
          'Appointment'
        ],
        ['/clinical-notes/tests/notifications?onlyFuture=true', 'Test'],
        ['/clinical-notes/referrals/notifications?onlyFuture=true', 'Referral'],
        ['/clinical-notes/contacts/notifications?onlyFuture=true', 'Contact'],
        [
          '/clinical-notes/clinical-notes/notifications?onlyFuture=true',
          'Clinical Note'
        ],
        ['/clinical-notes/samples/notifications?onlyFuture=true', 'Sample'],
        ['/clinical-notes/consents/notifications?onlyReminders=true', 'Consent']
      ];

      const results = await Promise.allSettled(
        endpoints.map(([url]) => axios.get(url))
      );

      const all = results.flatMap((result, i) => {
        if (result.status !== 'fulfilled') {
          console.error(
            `Failed to fetch ${endpoints[i][1]} notifications:`,
            result.reason
          );
          return [];
        }
        return normalize(result.value.data).map((n) => ({
          ...n,
          type: endpoints[i][1]
        }));
      });

      setNotifications(all);
    };

    fetchNotifications();
  }, [user?._id]);

  // ----------------------
  // 其他现有逻辑
  // ----------------------
  const [anchorEl, setAnchorEl] = React.useState(null);
  const isMenuOpen = Boolean(anchorEl);

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const clearSession = () => {
    setAnchorEl(null);
    axios.get(APICONSTANTS.logout).then(() => {
      navigate(CONSTANTS.signinPath);
      setUser(null);
      localStorage.setItem('2FAloggedin', 'false');
      // Drop the open pedigree tabs (TGCLOUD-1618). sessionStorage is per browser
      // TAB and is cleared only when the tab closes, so without this the open set
      // survives into the next login here. The snapshot is owner-scoped and would
      // be refused anyway; clearing it means it is not sitting there at all.
      clearWorkspace();
    });
  };

  // Release notes for the build this user is on. Fetched once; the same response
  // supplies both the unread cue and the dialog's contents.
  const releaseNotes = useReleaseNotes({ enabled: Boolean(user?._id) });
  const [isWhatsNewOpen, setWhatsNewOpen] = React.useState(false);

  const openWhatsNew = () => {
    setAnchorEl(null);
    setWhatsNewOpen(true);
    releaseNotes.markSeen();
  };

  const menuId = 'primary-search-account-menu';
  const renderMenu = (
    <Menu
      anchorEl={anchorEl}
      anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      id={menuId}
      open={isMenuOpen}
      onClose={handleMenuClose}
    >
      {user?.email && (
        <Typography
          variant="caption"
          style={{ paddingLeft: 15, paddingRight: 15 }}
          sx={{
            display: 'block'
          }}
        >
          {t(`Logged In as`)}:
        </Typography>
      )}
      {user?.email && (
        <Typography
          variant="caption"
          style={{ paddingLeft: 15, paddingRight: 15 }}
          sx={{
            display: 'block'
          }}
        >
          {user.email}
        </Typography>
      )}
      {props.menuItems &&
        React.cloneElement(props.menuItems, { handleMenuClose })}
      <MenuItem onClick={openWhatsNew}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            width: '100%'
          }}
        >
          {t(`What's new`)}
          {releaseNotes.unread && (
            <Box
              aria-label="Unread release notes"
              sx={{
                width: 7,
                height: 7,
                borderRadius: '50%',
                bgcolor: '#f59e0b',
                flexShrink: 0
              }}
            />
          )}
        </Box>
      </MenuItem>
      <MenuItem onClick={clearSession}>{t(`Log Out`)}</MenuItem>
    </Menu>
  );

  const notificationMenuId = 'primary-notifications-menu';

  // Logged-in users always see the bundled TrakGene logo. Custom per-
  // User-Group logos are reserved for patient flows (FHQ / custom-form
  // access pages) where branding matches the issuing clinic. The gate is
  // UserContext.user: truthy = logged-in clinician/admin, null = patient
  // inside the questionnaire shell.
  const displayedLogo = !user && logoURL && logoName ? logoURL : logo;

  return (
    <>
      <StyledAppBar
        position="fixed"
        navbar={navbar}
        appearance={appearance}
        navbarText={navbarText}
        offset={offset}
      >
        <SystemMessage>
          <AnnouncementsBanner />
        </SystemMessage>
        <Toolbar>
          {isMinimal ? (
            <Logo alt="Logo" src={displayedLogo} />
          ) : (
            <>
              <Box sx={{ display: { xs: 'inline-flex', lg: 'none' }, mr: 0.5 }}>
                <IconButton
                  onClick={props.onToggleMobileNav}
                  sx={{ color: navbarText ?? appearance.text }}
                  aria-label="Toggle navigation menu"
                >
                  {props.mobileNavOpen ? <CloseIcon /> : <MenuIcon />}
                </IconButton>
              </Box>
              <Logo alt="Logo" src={displayedLogo} />
              <PEP variant="h5">
                <StyledLink
                  to={CONSTANTS.adminPath}
                  appearance={appearance}
                  navbarText={navbarText}
                >
                  <SmallLogo alt="TrakGene" src={displayedLogo} />
                  {props.title ? t(props.title) : t('')}
                </StyledLink>
              </PEP>

              <Root />

              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  '& .MuiIconButton-root': { padding: '6px' }
                }}
              >
                <TranslationMenu color={navbarText} />

                {/* Global quick-actions menu (icon-only bolt). Self-gates on the
                    authenticated user and the FEATURE_GLOBAL_QUICK_ACTIONS flag,
                    so it renders nothing until that flag is on. */}
                <QuickActionsMenu systemFeatures={props.systemFeatures} />

                {user && (
                  <IconButton
                    data-va-anchor="topbar.calendar"
                    aria-label="Calendar"
                    onClick={() => navigate(CONSTANTS.calendarPath)}
                    color="inherit"
                    fontSize="large"
                    size="large"
                  >
                    <EventIcon />
                  </IconButton>
                )}

                {user && (
                  <IconButton
                    color="inherit"
                    data-va-anchor="topbar.notifications"
                    aria-label="Notifications"
                    onClick={handleNotificationsOpen}
                    fontSize="large"
                    size="large"
                  >
                    <Badge
                      badgeContent={displayedNotifications.length}
                      color="error"
                    >
                      <NotificationsIcon />
                    </Badge>
                  </IconButton>
                )}

                {/* The Copilot is always available — it runs guided
                    walkthroughs with no external AI call — so the header help
                    slot always opens it. The separate tour button this replaced
                    is gone: with the Copilot always present, the welcome tour is
                    offered inside it, and two help affordances side by side just
                    made the user choose between them.

                    Nothing renders outside a CopilotProvider — the patient
                    surface — because that is where the walkthrough state and the
                    overlay live, so a launcher there could not run anything. */}
                {copilot.available ? (
                  <IconButton
                    onClick={copilot.open}
                    color="inherit"
                    size="large"
                    aria-label={t('Open Copilot assistant')}
                    data-va-anchor="topbar.copilot"
                  >
                    <AssistantIcon />
                  </IconButton>
                ) : null}

                {user && (
                  <Box sx={{ ml: 2 }}>
                    <UserGroupChipCluster
                      user={user}
                      faviconUrl={faviconURL || null}
                      navbarColor={navbar ?? appearance?.color}
                      accentColor={accent}
                      avatarSlot={
                        <IconButton
                          aria-label="account of current user"
                          aria-controls={menuId}
                          aria-haspopup="true"
                          onClick={handleProfileMenuOpen}
                          color="inherit"
                          size="large"
                        >
                          {/* The unread cue has to live on the avatar, not only
                              on the menu item: a dot inside a closed menu is a
                              dot nobody sees. Same amber as the Updates tab. */}
                          <Badge
                            variant="dot"
                            invisible={!releaseNotes.unread}
                            sx={{
                              '& .MuiBadge-badge': { bgcolor: '#f59e0b' }
                            }}
                          >
                            {user.profilePhoto ? (
                              <Avatar
                                src="/api/users/profile-photo"
                                sx={{ width: 36, height: 36 }}
                              />
                            ) : (
                              <AccountCircle />
                            )}
                          </Badge>
                        </IconButton>
                      }
                    />
                  </Box>
                )}
              </Box>
            </>
          )}
        </Toolbar>
      </StyledAppBar>
      <StyledMenu
        anchorEl={anchorElNotifications}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        id={notificationMenuId}
        open={isNotificationsMenuOpen}
        onClose={handleNotificationsClose}
        appearance={appearance}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            padding: '10px 16px',
            gap: 12
          }}
        >
          <div style={{ flex: 1 }} />
          <Typography
            variant="caption"
            sx={{
              fontWeight: 'bold'
            }}
          >
            {showAllNotifications ? 'All Notifications' : 'My Notifications'}
          </Typography>
          <Switch
            size="small"
            checked={showAllNotifications}
            onChange={(e) => setShowAllNotifications(e.target.checked)}
            slotProps={{
              input: {
                'aria-label': showAllNotifications
                  ? 'Show all notifications'
                  : 'Show only my notifications'
              }
            }}
          />
        </div>
        <Divider />

        {displayedNotifications.length === 0 && (
          <MenuItem disabled>
            <Typography variant="body2">No upcoming reminders.</Typography>
          </MenuItem>
        )}

        {displayedNotifications.map((notification) => {
          const color = NOTE_TYPE_COLOURS[notification.type] || '#888';
          const desc = getNotificationDescription(notification);
          const secondary = [
            notification.familyMemberId || notification.familyId
              ? (notification.familyMemberId || notification.familyId).replace(
                  /-\d+$/,
                  ''
                )
              : '',
            [notification.firstName, notification.lastName]
              .filter(Boolean)
              .join(' ')
          ]
            .filter(Boolean)
            .join(' · ');
          return (
            <Box
              key={`${notification.type}_${notification.id}`}
              onClick={() => handleOpenRecord(notification)}
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1,
                px: 1.5,
                py: 1,
                borderLeft: `3px solid ${color}`,
                cursor: 'pointer',
                bgcolor: alpha(color, 0.08),
                '&:hover': { bgcolor: alpha(color, 0.14) },
                '& + .MuiBox-root': { borderTop: '1px solid #f0f0ee' }
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  sx={{
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: '0.06em',
                    textTransform: 'uppercase',
                    color,
                    lineHeight: 1
                  }}
                >
                  {notification.type}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ mt: 0.5, fontWeight: 500, lineHeight: 1.3 }}
                >
                  {desc}
                </Typography>
                {secondary && (
                  <Typography sx={{ fontSize: 12, color: '#888', mt: 0.25 }}>
                    {secondary}
                  </Typography>
                )}
              </Box>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDismissNotification(notification.id);
                }}
                sx={{ mt: -0.5 }}
              >
                <CloseIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Box>
          );
        })}
      </StyledMenu>
      {renderMenu}
      <WhatsNewDialog
        open={isWhatsNewOpen}
        onClose={() => setWhatsNewOpen(false)}
        releases={releaseNotes.releases}
        loading={releaseNotes.loading}
      />
      <Offset offset={offset} />
      <Modal
        open={isModalOpen}
        onClose={() => setOpenModal(false)}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <CustomDialog
          maxWidth="sm"
          title={
            <ModalHeader
              title={`Edit Appointment`}
              // personSummary={personSummary}
            />
          }
          open={isModalOpen}
          handleClose={handleClose}
          minHeight="auto"
          onClose={true}
        >
          <AppointmentForm2 _id={currentModel.id} />
        </CustomDialog>
      </Modal>
    </>
  );
});
